import { Fragment, useState, useEffect, useCallback, useMemo } from "react";
import BaseCarousel from "./common/BaseCarousel"; // Adjust path as needed
import RecurringDetailsModal from "../popup/finances/RecurringDetailsModal";
import {
  formatCurrency,
  mapRecurringTransactionToRow,
} from "../popup/finances/utils/helpers";
import { getRecurringTransactions } from "../../../api/finances/recurringTransactionsAPI";
import RecurringTransactionCard from "../cards/finances/RecurringTransactionCard";
import StandardEmpty from "../empty/StandardEmpty";
import GhostTransactionsCarousel from "../loading/skeletons/GhostTransactionCarousel";
import { useRefreshOnAuth } from "../../../hooks/useRefreshOnAuth";
import LoadingToRealTransitionWrapper from "../transitions/LoadingToRealTransitionWraper";
import { getLanguageContent } from "../../../utils/language/languageSelector";
import { PERSONAL_FINANCES_SCHEMA } from "../../../utils/schemas/language/pages/personalFinancesSchema";

function UpcomingRecurrentTransactionsCarousel() {
  const content = getLanguageContent(PERSONAL_FINANCES_SCHEMA);
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeWindowDays, setActiveWindowDays] = useState(7);
  const [activeStreamType, setActiveStreamType] = useState("OUTFLOW");
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [transactionDetailsModal, setTransactionDetailsModal] = useState(false);

  const fetchUpcoming = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await getRecurringTransactions({
        upcoming: true,
        days: activeWindowDays,
      });
      const mapped = Array.isArray(data?.upcoming)
        ? data.upcoming.map(mapRecurringTransactionToRow)
        : [];
      setRows(mapped);
    } catch (error) {
      console.error("Failed to fetch upcoming transactions", error);
    } finally {
      setIsLoading(false);
    }
  }, [activeWindowDays]);

  useRefreshOnAuth(fetchUpcoming);
  useEffect(() => {
    fetchUpcoming();
  }, [fetchUpcoming]);

  const filteredRows = useMemo(() => {
    return rows.filter((row) => row.stream_type === activeStreamType);
  }, [rows, activeStreamType]);

  const totals = useMemo(() => {
    const spending = rows
      .filter((r) => r.stream_type === "OUTFLOW")
      .reduce((acc, curr) => acc + (curr.amount_value || 0), 0);
    const income = rows
      .filter((r) => r.stream_type === "INFLOW")
      .reduce((acc, curr) => acc + (curr.amount_value || 0), 0);
    return {
      spending: formatCurrency(spending),
      income: formatCurrency(income),
      spendingCount: rows.filter((r) => r.stream_type === "OUTFLOW").length,
      incomeCount: rows.filter((r) => r.stream_type === "INFLOW").length,
    };
  }, [rows]);

  const openTransactionDetailsModal = (item) => {
    setSelectedTransaction(item);
    setTransactionDetailsModal(true);
  };

  const handleTransactionUpdate = (updatedTransaction) => {
    setSelectedTransaction(updatedTransaction);
    setRows((currentRows) =>
      currentRows.map((row) =>
        row.uuid === updatedTransaction.uuid
          ? { ...row, ...updatedTransaction }
          : row,
      ),
    );
  };

  return (
    <Fragment>
      <LoadingToRealTransitionWrapper
        isLoading={isLoading}
        ghost={
          <BaseCarousel
            isLoading={true}
            GhostComponent={<GhostTransactionsCarousel count={2} />}
          />
        }
      >
        <div className="upcoming-transactions-carousel-container mobile-reset-standard-card-container">
          {/* PARENT CONTROLLED HEADER */}
          <div className="utc-header">
            <div className="utc-header-left">
              <h4 className="carousel-title margin-reset">
                {content.recurring.upcoming.title}
              </h4>
              <div className="utc-header-forecast">
                {/* <img
                  src={`${process.env.PUBLIC_URL}/images/icons/calendar_light_blue.svg`}
                  alt="Forecast"
                /> */}
                <p>
                  {activeStreamType === "OUTFLOW"
                    ? content.recurring.upcoming.expected_spending
                    : content.recurring.upcoming.expected_income}
                  :{" "}
                  <strong>
                    {activeStreamType === "OUTFLOW"
                      ? totals.spending
                      : totals.income}
                  </strong>
                </p>
              </div>
            </div>
            <div className="utc-header-right">
              <div className="utc-header-filtering">
                {[7, 30].map((d) => (
                  <div
                    key={d}
                    className={`utc-header-filtering-item ${
                      activeWindowDays === d ? "active" : ""
                    }`}
                    onClick={() => setActiveWindowDays(d)}
                    style={{ cursor: "pointer" }}
                  >
                    {d}d
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="utc-body">
            {/* PARENT CONTROLLED SEGMENTS */}
            <div className="utc-body-segments-wrapper">
              <div className="utc-body-segments">
                <div className="segment-menu">
                  <div className="segment-container">
                    <div className="segment-buttons">
                      <span
                        className={`segment-button segment-button-reduced ${
                          activeStreamType === "OUTFLOW" ? "active" : ""
                        }`}
                        onClick={() => setActiveStreamType("OUTFLOW")}
                        style={{ cursor: "pointer" }}
                      >
                        {content.recurring.upcoming.spending_tab}{" "}
                        <strong className="circle-number">
                          {totals.spendingCount}
                        </strong>
                      </span>
                      <span
                        className={`segment-button segment-button-reduced ${
                          activeStreamType === "INFLOW" ? "active" : ""
                        }`}
                        onClick={() => setActiveStreamType("INFLOW")}
                        style={{ cursor: "pointer" }}
                      >
                        {content.recurring.upcoming.income_tab}{" "}
                        <strong className="circle-number">
                          {totals.incomeCount}
                        </strong>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="segment-border-line"></div>
            </div>

            {/* BASE CAROUSEL ENGINE (NO ARROWS, NO TITLE, NO BORDER) */}
            <BaseCarousel
              items={filteredRows}
              showBorder={false}
              emptyMsg={content.recurring.upcoming.empty}
              previousAlt={content.global.previous}
              nextAlt={content.global.next}
              renderItem={(item, index) => (
                <div className="utc-body-cards" key={item.id || index}>
                  <RecurringTransactionCard
                    transaction={item}
                    onClick={() => openTransactionDetailsModal(item)}
                  />
                </div>
              )}
            />
          </div>
        </div>
      </LoadingToRealTransitionWrapper>

      {transactionDetailsModal && (
        <RecurringDetailsModal
          transaction={selectedTransaction}
          onTransactionUpdate={handleTransactionUpdate}
          copy={content.recurring.details}
          commonCopy={content.global}
          onClose={() => setTransactionDetailsModal(false)}
        />
      )}
    </Fragment>
  );
}

export default UpcomingRecurrentTransactionsCarousel;
